<template>
  <div
    class="stretch bg-lm relative flex max-w-sm grow basis-80 rounded-b-2xl transition-shadow"
    :class="{
      'ring-2': isFramed,
      'rounded-t-2xl': !isHighlighted || isLoading,
      'ring-primary drop-shadow-md': isHighlighted && isFramed && !isLoading,
      'ring-lm-contrast/5 dark:ring-dm-contrast/10 dark:bg-dm-contrast/5':
        !isHighlighted || isLoading,
      'hover:ring-lm-contrast/10 dark:hover:ring-dm-contrast/20':
        !isHighlighted && !isLoading,
    }"
  >
    <div
      class="flex grow flex-col justify-between rounded-b-2xl"
      :class="{
        'p-8 py-10 xl:p-10': isFramed,
        'bg-primary/2 dark:bg-dm': isHighlighted && !isLoading,
        'rounded-t-2xl': !isHighlighted && !isLoading,
      }"
    >
      <!-- HIGHLIGHTED TAG -->

      <p
        v-if="!isLoading && isHighlighted && isFramed"
        class="bg-primary text-primary-contrast ring-primary absolute left-0 top-0 z-10 w-full -translate-y-full rounded-t-2xl px-2.5 py-2.5 text-center text-sm font-semibold uppercase leading-5 ring-2"
      >
        {{ highlightText || $t("plan_card.most_popular") }}
      </p>

      <div>
        <!-- TITLE -->

        <template v-if="hasSkeletons || !computedTitle">
          <UpmSkeletonWrapper :active="isLoading">
            <UpmSkeleton class="h-8 w-40 max-w-full" />
          </UpmSkeletonWrapper>
        </template>

        <template v-else>
          <h3 :class="['text-auto text-2xl font-semibold leading-8']">
            <slot name="title">{{ computedTitle }}</slot>
          </h3>
        </template>

        <!-- DESCRIPTION -->

        <template v-if="showDescription">
          <template v-if="hasSkeletons || !computedDesc">
            <UpmSkeletonWrapper :active="isLoading" class="mt-6 space-y-2">
              <UpmSkeleton class="h-4" />
              <UpmSkeleton class="h-4" />
              <UpmSkeleton class="h-4" />
              <UpmSkeleton class="h-4 w-2/3" />
            </UpmSkeletonWrapper>
          </template>

          <template v-else>
            <p class="text-auto-50 mt-4 line-clamp-4">
              <slot name="description">{{ computedDesc }}</slot>
            </p>
          </template>
        </template>
      </div>

      <div>
        <!-- PRICE -->

        <template v-if="hasSkeletons || !computedPrice">
          <UpmSkeletonWrapper :active="isLoading" class="mt-12">
            <UpmSkeleton class="mb-2 h-4 w-16" />
            <div class="mb-2 flex items-end gap-x-2">
              <UpmSkeleton class="h-8 w-24" />
              <UpmSkeleton class="h-4 w-6" />
            </div>
            <UpmSkeleton class="mt-6 h-4 w-40" />
          </UpmSkeletonWrapper>
        </template>

        <template v-else>
          <div class="mt-12">
            <!-- REGULAR PRICE -->

            <template v-if="!!product?.hasDiscount">
              <p class="relative inline-flex">
                <em class="text-auto-50 text-sm line-through">{{
                  $t("plan_card.was_price", {
                    price: showLMFP
                      ? product?.regularMonthlyFromPrice.toString()
                      : product?.regularPrice.toString(),
                  })
                }}</em>

                <UpmDiscountTag
                  v-if="!!product?.hasDiscount"
                  class="absolute -right-3 -top-2 origin-left translate-x-full -rotate-12"
                  >{{
                    $t("plan_card.percentage_off", {
                      percentage: product?.currentSaving,
                    }).toUpperCase()
                  }}</UpmDiscountTag
                >
              </p>
            </template>

            <!-- CURRENT PRICE INTRO -->

            <template v-else>
              <p class="text-auto-50 text-sm">
                {{
                  product?.hasBillingCycle
                    ? $t("plan_card.from")
                    : $t("plan_card.only")
                }}
              </p>
            </template>

            <!-- CURRENT PRICE -->

            <p class="flex items-baseline gap-x-1">
              <span class="text-auto text-4xl font-bold tracking-tight">
                <slot name="price">{{ computedPrice }}</slot>
              </span>

              <!-- CURRENT PRICE SUFFIX -->

              <template v-if="computedPriceSuffix">
                <span class="text-auto-50 leading-6">{{
                  computedPriceSuffix
                }}</span>
              </template>
            </p>

            <!-- CURRENT PRICE SUMMARY -->

            <template v-if="showSummary && computedPriceSummary">
              <p class="text-auto-50 mt-1.5 text-sm">
                {{ computedPriceSummary }}
              </p>
            </template>
          </div>
        </template>

        <!-- CTA -->

        <UpmSkeletonWrapper
          v-if="hasSkeletons"
          :active="isLoading"
          class="mt-6"
        >
          <UpmSkeleton class="h-12 w-full !rounded-full" />
        </UpmSkeletonWrapper>

        <UpmButton
          v-else
          as="anchor"
          :href="computedHref"
          class="border-2x mt-6 w-full justify-center !rounded-full py-3 font-bold shadow-md"
          @click="emitCtaClick"
          :class="{
            'is-primary': isHighlighted,
            'is-dark dark:is-light': !isHighlighted,
          }"
          >{{ computedCta }}</UpmButton
        >

        <!-- FEATURES -->

        <template v-if="hasFeatures">
          <div class="border-t-1 border-lm-contrast border-solid pt-4">
            <UpmSkeletonWrapper
              v-if="hasSkeletons"
              :active="isLoading"
              class="mt-6 space-y-3"
            >
              <div class="flex gap-x-2">
                <UpmSkeleton class="h-4 w-4" />
                <UpmSkeleton class="h-4 w-2/6" />
              </div>
              <div class="flex gap-x-2">
                <UpmSkeleton class="h-4 w-4" />
                <UpmSkeleton class="h-4 w-4/6" />
              </div>
              <div class="flex gap-x-2">
                <UpmSkeleton class="h-4 w-4" />
                <UpmSkeleton class="h-4 w-3/6" />
              </div>
              <div class="flex gap-x-2">
                <UpmSkeleton class="h-4 w-4" />
                <UpmSkeleton class="h-4 w-4/6" />
              </div>
              <div class="flex gap-x-2">
                <UpmSkeleton class="h-4 w-4" />
                <UpmSkeleton class="h-4 w-4/6" />
              </div>
            </UpmSkeletonWrapper>

            <div v-else class="text-lm-contrast mt-6 dark:text-white">
              <slot name="features">
                <ul role="list" class="space-y-2 leading-6">
                  <li
                    v-for="({ label, included }, fI) in features"
                    :key="`f-${fI}`"
                    class="flex items-center gap-x-2"
                  >
                    <CheckIcon
                      v-if="included ?? true"
                      class="text-success h-4 w-4 flex-none"
                      aria-hidden="true"
                    />
                    <XMarkIcon
                      v-else
                      class="text-auto-25 h-4 w-4 flex-none"
                      aria-hidden="true"
                    />
                    <UpmMarkdown
                      :class="{
                        'text-auto-50': !(included ?? true),
                      }"
                      >{{ label }}</UpmMarkdown
                    >
                  </li>
                </ul>
              </slot>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { XMarkIcon, CheckIcon } from "@heroicons/vue/20/solid";
import UpmButton from "@src/components/UpmButton.vue";
import UpmMarkdown from "@src/components/UpmMarkdown.vue";
import UpmSkeleton from "@src/components/UpmSkeleton.vue";
import UpmSkeletonWrapper from "@src/components/UpmSkeletonWrapper.vue";
import { useI18n } from "@src/composables/i18n";
import { computed, inject, ref, useSlots } from "vue";
import { useCatalogue } from "@src/composables/catalogue";
import _isEmpty from "lodash/isEmpty";
import _pick from "lodash/pick";
import { IUpmEvent } from "@src/models/UpmEvent";
import UpmDiscountTag from "@src/components/UpmDiscountTag.vue";
import { useMoney } from "@src/composables/utils";

const props = withDefaults(
  defineProps<{
    coupons?: string[];
    ctaText?: string;
    currencyCode?: string;
    description?: string;
    features?: { label: string; included: boolean }[];
    highlightText?: string;
    href?: string;
    id?: string;
    isFramed?: boolean;
    isHighlighted?: boolean;
    orderConfigUrl?: string;
    price?: string;
    priceDiscounted?: string;
    priceSuffix?: string;
    priceSummary?: string;
    showDescription?: boolean;
    showLMFP?: boolean; // Show [L]owest [M]onthly [F]rom [P]rice
    showSummary?: boolean;
    showZeroAsFree?: boolean;
    title?: string;
    trimTrailingZeroes?: boolean;
  }>(),
  {
    ctaText: "",
    currencyCode: "",
    description: "",
    features: () => [],
    highlightText: "",
    href: "",
    id: "",
    isFramed: true,
    isHighlighted: false,
    price: "",
    orderConfigUrl: "",
    priceDiscounted: "",
    priceSuffix: "",
    priceSummary: "",
    showDescription: true,
    showLMFP: true,
    showSummary: true,
    showZeroAsFree: true,
    title: "",
    trimTrailingZeroes: false,
  }
);

const { $t, $tc } = useI18n({ packs: ["plan_card", "core"] });
const { removeTrailingZeroes } = useMoney();

const { getProduct, setCurrencyFilter, setCouponsFilter } = useCatalogue({
  locale: ref(inject("locale", "en")),
  currencyCode: props.currencyCode,
  orderConfigUrl: props.orderConfigUrl,
  showLMFP: props.showLMFP,
  showZeroAsFree: props.showZeroAsFree,
  trimTrailingZeroes: props.trimTrailingZeroes,
});

const slots = useSlots();

const {
  data: product,
  isError,
  isFetching,
} = getProduct(props.id, [
  setCurrencyFilter(props.currencyCode),
  setCouponsFilter(props.coupons),
]);

const emit = defineEmits<{
  (event: "upmEvent", payload: IUpmEvent): void;
}>();

const emitCtaClick = (event: IUpmEvent["event"]) =>
  emit("upmEvent", {
    event,
    name: "ctaClick",
    payload: {
      id: props.id,
    },
  });

const isLoading = computed(() => isFetching.value);

const hasSkeletons = computed(
  () => isError.value || (props.id && _isEmpty(product.value))
);

const computedTitle = computed(() => props.title || product.value?.name);

const computedDesc = computed(
  () =>
    props.description || product.value?.excerpt || product.value?.description
);

const computedPrice = computed(() => {
  if (props.price) return props.price;
  return props.showLMFP
    ? product?.value?.currentMonthlyFromPrice
    : product?.value?.currentPrice;
});

const computedPriceSuffix = computed(() => {
  if (props.priceSuffix) return props.priceSuffix;
  if (props.showLMFP && product?.value?.hasBillingCycle)
    return ["/", $tc("core.n_months", 1)].join(" ");
  return;
});

const computedPriceSummary = computed(() => {
  if (props.priceSummary) return props.priceSummary;
  if (!product?.value?.hasBillingCycle) return undefined;
  return $t(
    product?.value?.hasDiscount
      ? "plan_card.discounted_subscription_summary"
      : "plan_card.non_discounted_subscription_summary",
    {
      numericTerm: product?.value?.billingCycle()?.numeric,
      descriptiveTerm: product?.value?.billingCycle()?.descriptive,
      currentPrice: removeTrailingZeroes(product?.value?.currentPrice),
      regularPrice: removeTrailingZeroes(product?.value?.regularPrice),
    }
  );
});

const computedHref = computed(() => {
  const href = props.href || product.value?.configUrl?.href;
  try {
    const url = new URL(href as string);
    // We check hostname to ensure we don't append to eg `tel:` or `mailto:` links
    if (url?.hostname && props.currencyCode)
      url.searchParams.set("currency", props.currencyCode);
    return url.toString();
  } catch {
    return href;
  }
});

const computedCta = computed(
  () => props.ctaText || $t("plan_card.get_started")
);

const hasFeatures = computed(
  () => Object.keys(slots).includes("features") || !!props.features.length
);
</script>
